import {
  ComponentClass,
  createElement,
  FunctionComponent,
  memo,
  NamedExoticComponent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import { ScrollEventCallback } from "overlayscrollbars";
import { getPortalComponents } from "../../../../generic";
import { ScrollbarType } from "../../../../ui";
import { TabsContextProvider } from "./TabsContext";
import { TabsHeadersContainer } from "./TabsHeader";
import { TabStrategyType } from "./types";

import styles from "./style.module.scss";

const { Container, PortalContentProvider } = getPortalComponents();
export const TabsContainer = Container;

export type TabsProps = {
  children: ReactNode[] | ReactNode,
  className?: string,
  tabsHeadersContainerClassName?: string,
  tabsContainerClassName?: string,
  defaultActiveTabId?: string,
  strategy?: TabStrategyType,
  tabHeadersContentProviderComponent?: string | FunctionComponent | ComponentClass | NamedExoticComponent
  tabHeadersContainerComponent?: string | FunctionComponent | ComponentClass | NamedExoticComponent
}

export const Tabs = memo<TabsProps>((
  {
    children,
    className,
    tabsHeadersContainerClassName,
    tabsContainerClassName,
    defaultActiveTabId,
    strategy = "collapse",
    tabHeadersContainerComponent,
    tabHeadersContentProviderComponent = PortalContentProvider,
  }) => {

  const [ rightArrowDisabled, setRightArrowDisabled ] = useState(true);
  const [ leftArrowDisabled, setLeftArrowDisabled ] = useState(true);

  const tabsRef = useRef<ScrollbarType>();

  useEffect(() => {
    if (tabsRef.current) {
      setLeftArrowDisabled(!tabsRef?.current?.osInstance()
        ?.getState()?.hasOverflow?.x);
    }
  }, [ children ]);

  const onArrowClick = useCallback((direction: string) => {
    const viewport = tabsRef.current?.osInstance()
      ?.getElements().viewport as Element;
    if (viewport) {
      direction === "left"
        ? tabsRef?.current?.osInstance()
          ?.scroll({ left: viewport.scrollLeft + 150 }, 100)
        : tabsRef?.current?.osInstance()
          ?.scroll({ left: viewport.scrollLeft - 150 }, 100);
    }
  }, []);

  const onTabsScroll = useCallback<ScrollEventCallback>((e) => {
    if (e?.target) {
      const target = e?.target as Element;

      if (target.scrollLeft) {
        setRightArrowDisabled(false);
      } else {
        setRightArrowDisabled(true);
      }

      if ((target.scrollWidth - target.clientWidth) > Math.ceil(target.scrollLeft)) {
        setLeftArrowDisabled(false);
      } else {
        setLeftArrowDisabled(true);
      }
    }
  }, []);

  return (
    <TabsContextProvider strategy={strategy!} defaultActiveTabId={defaultActiveTabId}>
      <div className={classNames(styles.root, className)}>
        {
          createElement(
            tabHeadersContentProviderComponent,
            {},
            <TabsHeadersContainer
              onScroll={onTabsScroll}
              tabsRef={tabsRef}
              disabledRightArrow={rightArrowDisabled}
              disabledLeftArrow={leftArrowDisabled}
              onArrowClick={onArrowClick}
              className={tabsHeadersContainerClassName}
              tabsContainerClassName={tabsContainerClassName}
              containerComponent={tabHeadersContainerComponent}
            />,
          )
        }
        {children}
      </div>
    </TabsContextProvider>
  );
});

export default Tabs;


